import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Sun, Moon, Menu, X, ChevronDown, ChevronRight, BookOpen, Play, GraduationCap, ShieldCheck } from 'lucide-react';
import { trackTopNavAssessmentClick } from '../utils/analytics';

/* ─── Navigation Structure ─────────────────────────────────────── */

const SOLUTIONS_LINKS = [
  { label: 'Find Your Platform', path: '/compliance-training-assessment', desc: 'Interactive assessment tool' },
  { label: 'Credential Pathways', path: '/credentials', desc: 'Credential progression families' },
  { label: 'Features', path: '/features', desc: 'Full platform capabilities' },
  { label: 'About', path: '/about', desc: 'Our story and mission' },
];

const LIBRARY_LINKS = [
  { label: 'Standard Library', path: '/library', desc: 'Ready-made compliance courses' },
  { label: 'Specialist Programmes', path: '/library/programmes', desc: 'Credentialed pathways' },
];

const COMPARE_LINKS = [
  { label: 'Find Your Platform', path: '/compliance-training-assessment', desc: 'Free 3-minute platform matcher' },
  { label: 'Best Compliance Training Platforms', path: '/best-compliance-training-platforms', desc: 'Compare all platforms' },
  { label: 'Health & Safety Training Providers', path: '/compare/health-safety-training-providers', desc: 'H&S training platforms' },
  { label: 'Compliance & GRC Software', path: '/compare/corporate-compliance-training-platforms', desc: 'Ethics & compliance platforms' },
  { label: 'LMS & HR Training Platforms', path: '/compare/learning-management-systems', desc: 'LMS platforms' },
  { label: 'ESG & Sustainability Platforms', path: '/compare/esg-sustainability-platforms', desc: 'ESG training platforms' },
  { label: 'General Training Platforms', path: '/compare/general-training-platforms', desc: 'Course libraries & marketplaces' },
];

const RESOURCES_LINKS = [
  { label: 'Knowledge Hub', path: '/knowledge-hub', desc: 'Articles, guides & insights' },
  { label: 'Support', path: '/support', desc: 'Help & contact' },
];

const TRUST_LINKS = [
  { label: 'Core Compliant Accreditation', path: '/accreditation', desc: 'Independent accreditation & verification' },
  { label: 'Accreditation Registry', path: '/accreditation/registry', desc: 'Browse accredited programmes' },
  { label: 'How Verification Works', path: '/verification', desc: 'Confirm any credential in seconds' },
  { label: 'Professional Standards Institute', path: '/professional-standards-institute', desc: 'CPFPSI — quality & credential framework' },
  { label: 'Verify a Credential', path: '/verify/sample', desc: 'Public verification & evidence pack' },
  { label: 'Trust Centre', path: '/trust', desc: 'Security, governance & procurement' },
];

/* ─── Dropdown Component ───────────────────────────────────────── */

function NavDropdown({ label, links, isActive, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timeoutRef = useRef(null);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={testId}
        aria-haspopup="true"
        aria-expanded={open}
        className={`px-3 py-2 rounded-lg text-[13px] font-semibold transition-all flex items-center gap-1 ${
          isActive
            ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/80 dark:bg-indigo-950/40'
            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800/60'
        }`}
      >
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1.5 left-0 w-72 bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-xl shadow-xl shadow-slate-900/8 dark:shadow-slate-950/40 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {links.map((link, i) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => {
                if (link.path === '/compliance-training-assessment') trackTopNavAssessmentClick();
                setOpen(false);
              }}
              data-testid={`${testId}-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`block w-full text-left px-4 py-3 transition-colors ${
                i < links.length - 1 ? 'border-b border-slate-100 dark:border-slate-800/60' : ''
              } hover:bg-slate-50 dark:hover:bg-slate-800/60`}
            >
              <p className="text-sm font-semibold text-slate-900 dark:text-white">{link.label}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{link.desc}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Main Navbar ──────────────────────────────────────────────── */

export default function PublicNavbar({ onSignIn }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);

  const handleSignIn = useCallback(() => {
    if (onSignIn) onSignIn();
    else navigate('/?intent=login#auth');
  }, [onSignIn, navigate]);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setMobileExpanded(null);
  }, []);

  const isSolutionsActive = ['/compliance-training-assessment', '/features', '/about', '/what-is-cpdforge'].some(p => pathname === p || pathname.startsWith(p));
  const isLibraryActive = pathname.startsWith('/library');
  const isCompareActive = pathname.includes('/compare') || pathname === '/best-compliance-training-platforms' || pathname.includes('-alternative');
  const isResourcesActive = ['/knowledge-hub', '/support'].some(p => pathname === p || pathname.startsWith(p));
  const isTrustActive = ['/professional-standards-institute', '/verify', '/trust'].some(p => pathname === p || pathname.startsWith(p));

  return (
    <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-200/60 dark:border-slate-800/60" data-testid="public-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">

        {/* Logo — real link to Home */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" data-testid="public-nav-logo" aria-label="CPDForge AI home">
          <img src="/cpdforge-icon.webp" alt="CPDForge AI" className="w-8 h-8 rounded-lg object-contain" />
          <span className="text-lg font-bold text-slate-900 dark:text-white tracking-tight hidden sm:block">CPDForge AI</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">

          {/* Solutions ▾ */}
          <NavDropdown
            label="Solutions"
            links={SOLUTIONS_LINKS}
            isActive={isSolutionsActive}
            testId="nav-solutions"
          />

          {/* Library ▾ — Standard Library + Specialist Programmes */}
          <NavDropdown
            label="Library"
            links={LIBRARY_LINKS}
            isActive={isLibraryActive}
            testId="nav-library"
          />

          {/* AI Governance — top-level flagship pillar, emerald accent */}
          <Link
            to="/ai"
            data-testid="nav-ai-governance"
            className={`px-3 py-2 rounded-lg text-[13px] font-bold transition-all flex items-center gap-1.5 ${
              pathname === '/ai' || pathname.startsWith('/ai/')
                ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50/80 dark:bg-emerald-950/40'
                : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50/60 dark:hover:bg-emerald-950/30'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            AI Governance
          </Link>

          {/* Compare ▾ */}
          <NavDropdown
            label="Compare"
            links={COMPARE_LINKS}
            isActive={isCompareActive}
            testId="nav-compare"
          />

          {/* Resources ▾ */}
          <NavDropdown
            label="Resources"
            links={RESOURCES_LINKS}
            isActive={isResourcesActive}
            testId="nav-resources"
          />

          {/* Trust ▾ */}
          <NavDropdown
            label="Trust"
            links={TRUST_LINKS}
            isActive={isTrustActive}
            testId="nav-trust"
          />

          {/* Pricing — top-level */}
          <Link
            to="/pricing"
            data-testid="nav-pricing"
            className={`px-3 py-2 rounded-lg text-[13px] font-semibold transition-all ${
              pathname === '/pricing'
                ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/80 dark:bg-indigo-950/40'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800/60'
            }`}
          >
            Pricing
          </Link>
        </nav>

        {/* Right side — CTAs */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            data-testid="public-theme-toggle"
          >
            {theme === 'dark'
              ? <Sun className="w-4 h-4 text-slate-400" />
              : <Moon className="w-4 h-4 text-slate-500" />}
          </button>

          <button
            onClick={handleSignIn}
            data-testid="public-nav-signin"
            className="hidden lg:block px-3.5 py-2 rounded-lg text-[13px] font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            Sign In
          </button>

          <Link
            to="/demo"
            data-testid="nav-view-demo"
            className="hidden lg:flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13px] font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-md shadow-indigo-500/20 transition-all"
          >
            <Play className="w-3 h-3 fill-current" />
            View Demo
          </Link>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            data-testid="nav-mobile-menu"
          >
            {mobileOpen
              ? <X className="w-5 h-5 text-slate-600 dark:text-slate-300" />
              : <Menu className="w-5 h-5 text-slate-600 dark:text-slate-300" />}
          </button>
        </div>
      </div>

      {/* ─── Mobile Navigation ──────────────────────────────────── */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-950 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-nav">
          <div className="px-4 py-3 space-y-0.5">

            {/* Library — prominent, top position */}
            <Link
              to="/library"
              onClick={closeMobile}
              data-testid="mobile-nav-library"
              className={`flex items-center gap-2.5 w-full text-left px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                pathname.startsWith('/library')
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40'
                  : 'text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50/60 dark:hover:bg-indigo-950/30'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Standard Library
            </Link>

            {/* Specialist Programmes — prominent */}
            <Link
              to="/library/programmes"
              onClick={closeMobile}
              data-testid="mobile-nav-programmes"
              className={`flex items-center gap-2.5 w-full text-left px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                pathname.startsWith('/library/programme')
                  ? 'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-950/40'
                  : 'text-violet-600 dark:text-violet-400 hover:bg-violet-50/60 dark:hover:bg-violet-950/30'
              }`}
            >
              <GraduationCap className="w-4 h-4" />
              Specialist Programmes
            </Link>

            {/* AI Governance — flagship pillar */}
            <Link
              to="/ai"
              onClick={closeMobile}
              data-testid="mobile-nav-ai-governance"
              className={`flex items-center gap-2.5 w-full text-left px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                pathname === '/ai' || pathname.startsWith('/ai/')
                  ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40'
                  : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50/60 dark:hover:bg-emerald-950/30'
              }`}
            >
              <ShieldCheck className="w-4 h-4" />
              AI Governance
            </Link>

            {/* Pricing */}
            <Link
              to="/pricing"
              onClick={closeMobile}
              data-testid="mobile-nav-pricing"
              className={`block w-full text-left px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                pathname === '/pricing'
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40'
                  : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60'
              }`}
            >
              Pricing
            </Link>

            {/* Solutions section */}
            <MobileSection
              label="Solutions"
              links={SOLUTIONS_LINKS}
              expanded={mobileExpanded === 'solutions'}
              onToggle={() => setMobileExpanded(mobileExpanded === 'solutions' ? null : 'solutions')}
              onItemClick={closeMobile}
              pathname={pathname}
              testId="mobile-nav-solutions"
            />

            {/* Compare section */}
            <MobileSection
              label="Compare"
              links={COMPARE_LINKS}
              expanded={mobileExpanded === 'compare'}
              onToggle={() => setMobileExpanded(mobileExpanded === 'compare' ? null : 'compare')}
              onItemClick={closeMobile}
              pathname={pathname}
              testId="mobile-nav-compare"
            />

            {/* Resources section */}
            <MobileSection
              label="Resources"
              links={RESOURCES_LINKS}
              expanded={mobileExpanded === 'resources'}
              onToggle={() => setMobileExpanded(mobileExpanded === 'resources' ? null : 'resources')}
              onItemClick={closeMobile}
              pathname={pathname}
              testId="mobile-nav-resources"
            />

            {/* Trust section */}
            <MobileSection
              label="Trust"
              links={TRUST_LINKS}
              expanded={mobileExpanded === 'trust'}
              onToggle={() => setMobileExpanded(mobileExpanded === 'trust' ? null : 'trust')}
              onItemClick={closeMobile}
              pathname={pathname}
              testId="mobile-nav-trust"
            />

            {/* CTAs */}
            <div className="pt-3 mt-2 border-t border-slate-200/60 dark:border-slate-800/60 space-y-2">
              <Link
                to="/demo"
                onClick={closeMobile}
                data-testid="mobile-nav-demo"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 shadow-md shadow-indigo-500/20"
              >
                <Play className="w-3.5 h-3.5 fill-current" />
                View Demo
              </Link>
              <button
                onClick={() => { handleSignIn(); setMobileOpen(false); }}
                data-testid="mobile-nav-signin"
                className="block w-full text-center px-4 py-3 rounded-lg text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-all"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* ─── Mobile Expandable Section ──────────────────────────────── */

function MobileSection({ label, links, expanded, onToggle, onItemClick, pathname, testId }) {
  return (
    <div>
      <button
        onClick={onToggle}
        data-testid={testId}
        aria-expanded={expanded}
        className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-all"
      >
        {label}
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="ml-4 pl-3 border-l-2 border-slate-200 dark:border-slate-800 space-y-0.5 pb-1">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => {
                if (link.path === '/compliance-training-assessment') trackTopNavAssessmentClick();
                if (onItemClick) onItemClick();
              }}
              data-testid={`${testId}-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all ${
                pathname === link.path
                  ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/80 dark:bg-indigo-950/40 font-semibold'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
